/**
 * The drawing over the camera (Sprint 103.0): the horizon, the day's path, its
 * hours, the reference days and the sun. Since Sprint 103.1 the two solstices
 * are always drawn, thinner and paler than the day, each labelled at its top. Colours are the design tokens, read
 * from the page's custom properties so the overlay follows the theme the rest
 * of the page does.
 */

import {
  project,
  type CameraBasis,
  type ScreenPoint,
  type SkyDirection,
  type Viewport
} from '../model/projection.ts';

export interface Palette {
  readonly sun: string;
  readonly sunStrong: string;
  readonly surface: string;
  readonly text: string;
  readonly muted: string;
  readonly skyTop: string;
  readonly skyGround: string;
}

export function readPalette(element: Element): Palette {
  const style = getComputedStyle(element);
  const read = (name: string): string => style.getPropertyValue(name).trim();
  return {
    sun: read('--color-sun'),
    sunStrong: read('--color-sun-strong'),
    surface: read('--color-bg-surface'),
    text: read('--color-text'),
    muted: read('--color-text-muted'),
    skyTop: read('--color-bg-selected'),
    skyGround: read('--color-bg-muted')
  };
}

export interface ReferenceLine {
  readonly path: readonly SkyDirection[];
  /** Written at the path's highest point: above it when `above`, below it otherwise. */
  readonly label?: string;
  readonly above?: boolean;
}

export interface Scene {
  readonly camera: CameraBasis;
  readonly viewport: Viewport;
  /** Without a camera picture the sky is drawn too: a plain sky and the ground. */
  readonly drawnSky: boolean;
  readonly path: readonly SkyDirection[];
  readonly hours: readonly (SkyDirection & { readonly label: string })[];
  readonly references: readonly ReferenceLine[];
  readonly sun: (SkyDirection & { readonly aboveHorizon: boolean }) | undefined;
  readonly cardinals: readonly { readonly azimuth: number; readonly label: string }[];
}

/** Points along a path, broken wherever the path goes behind the viewer. */
function runs(points: readonly ScreenPoint[], viewport: Viewport): ScreenPoint[][] {
  const limit = Math.max(viewport.width, viewport.height) * 4;
  const out: ScreenPoint[][] = [];
  let current: ScreenPoint[] = [];
  for (const point of points) {
    const usable = point.inFront && Math.abs(point.x) < limit && Math.abs(point.y) < limit;
    if (usable) {
      current.push(point);
    } else if (current.length > 0) {
      out.push(current);
      current = [];
    }
  }
  if (current.length > 0) out.push(current);
  return out;
}

function stroke(ctx: CanvasRenderingContext2D, run: readonly ScreenPoint[]): void {
  ctx.beginPath();
  run.forEach((point, index) =>
    index === 0 ? ctx.moveTo(point.x, point.y) : ctx.lineTo(point.x, point.y)
  );
  ctx.stroke();
}

function label(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  palette: Palette
): void {
  ctx.font = '600 12px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.lineWidth = 3;
  ctx.strokeStyle = palette.text;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = palette.surface;
  ctx.fillText(text, x, y);
}

/** Clear of the place, tape and chip above and of the readout and strip below. */
const LABEL_TOP = 190;
const LABEL_BOTTOM = 210;

function highestOnScreen(
  path: readonly SkyDirection[],
  at: (d: SkyDirection) => ScreenPoint,
  viewport: Viewport
): ScreenPoint | undefined {
  let best: { point: ScreenPoint; altitude: number } | undefined;
  for (const direction of path) {
    const p = at(direction);
    const visible =
      p.inFront &&
      p.x >= 24 &&
      p.x <= viewport.width - 24 &&
      p.y >= LABEL_TOP &&
      p.y <= viewport.height - LABEL_BOTTOM;
    if (visible && (best === undefined || direction.altitudeDegrees > best.altitude)) {
      best = { point: p, altitude: direction.altitudeDegrees };
    }
  }
  return best?.point;
}

export function draw(ctx: CanvasRenderingContext2D, scene: Scene, palette: Palette): void {
  const { camera, viewport } = scene;
  const at = (d: SkyDirection): ScreenPoint => project(d, camera, viewport);
  ctx.clearRect(0, 0, viewport.width, viewport.height);

  const horizon: ScreenPoint[] = [];
  for (let azimuth = 0; azimuth <= 360; azimuth += 2) {
    horizon.push(at({ azimuthDegrees: azimuth, altitudeDegrees: 0 }));
  }

  if (scene.drawnSky) {
    ctx.fillStyle = palette.skyTop;
    ctx.fillRect(0, 0, viewport.width, viewport.height);
    // The ground: everything under the horizon, closed along the screen's foot.
    ctx.fillStyle = palette.skyGround;
    for (const run of runs(horizon, viewport)) {
      ctx.beginPath();
      run.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      const last = run[run.length - 1]!;
      const first = run[0]!;
      ctx.lineTo(last.x, viewport.height * 4);
      ctx.lineTo(first.x, viewport.height * 4);
      ctx.closePath();
      ctx.fill();
    }
    ctx.strokeStyle = palette.muted;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 5]);
    for (const altitude of [30, 60]) {
      const ring: ScreenPoint[] = [];
      for (let azimuth = 0; azimuth <= 360; azimuth += 3)
        ring.push(at({ azimuthDegrees: azimuth, altitudeDegrees: altitude }));
      runs(ring, viewport).forEach((run) => stroke(ctx, run));
    }
  }

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = scene.drawnSky ? palette.muted : palette.surface;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([8, 6]);
  runs(horizon, viewport).forEach((run) => stroke(ctx, run));

  for (const cardinal of scene.cardinals) {
    const p = at({ azimuthDegrees: cardinal.azimuth, altitudeDegrees: 0 });
    if (p.inFront) label(ctx, cardinal.label, p.x, p.y + 6, palette);
  }

  ctx.setLineDash([2, 6]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = scene.drawnSky ? palette.muted : palette.surface;
  for (const reference of scene.references) {
    runs(reference.path.map(at), viewport).forEach((run) => stroke(ctx, run));
  }
  ctx.setLineDash([]);
  for (const reference of scene.references) {
    if (reference.label === undefined) continue;
    // At the path's highest point still on screen: the label says which line is which.
    const top = highestOnScreen(reference.path, at, viewport);
    if (top === undefined) continue;
    const x = Math.max(90, Math.min(viewport.width - 90, top.x));
    label(ctx, reference.label, x, reference.above === true ? top.y - 22 : top.y + 8, palette);
  }

  ctx.setLineDash([]);
  const path = runs(scene.path.map(at), viewport);
  ctx.strokeStyle = palette.text;
  ctx.globalAlpha = 0.25;
  ctx.lineWidth = 7;
  path.forEach((run) => stroke(ctx, run));
  ctx.globalAlpha = 1;
  ctx.strokeStyle = palette.sun;
  ctx.lineWidth = 3.5;
  path.forEach((run) => stroke(ctx, run));

  for (const hour of scene.hours) {
    const p = at(hour);
    if (!p.inFront) continue;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
    ctx.fillStyle = palette.surface;
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = palette.sunStrong;
    ctx.stroke();
    label(ctx, hour.label, p.x, p.y + 10, palette);
  }

  if (scene.sun !== undefined && scene.sun.aboveHorizon) {
    const p = at(scene.sun);
    if (p.inFront) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 32, 0, Math.PI * 2);
      ctx.fillStyle = palette.sun;
      ctx.globalAlpha = 0.3;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 19, 0, Math.PI * 2);
      ctx.fillStyle = palette.sun;
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = palette.surface;
      ctx.stroke();
    }
  }
}
